import { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';

interface ProfileLoadingTransitionProps {
  isVisible: boolean;
  businessName: string;
}

const LOADING_MESSAGES = [
  'Analyzing your responses...',
  'Learning about your business...',
  'Dreaming up some big ideas...',
  "Now I'm getting excited...",
  "I think you're onto something big here...",
  'Just a smidge more...',
  'Almost ready to show you...',
];

export function ProfileLoadingTransition({
  isVisible,
  businessName,
}: ProfileLoadingTransitionProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  useEffect(() => {
    if (!isVisible) {
      setCurrentMessageIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) =>
        prev < LOADING_MESSAGES.length - 1 ? prev + 1 : prev
      );
    }, 3500); // 3.5 seconds per message for a more relaxed pace

    return () => clearInterval(interval);
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-primary-50 via-white to-accent-50 z-50 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-6">
        {/* Animated icon */}
        <div className="relative mb-8">
          <div className="w-20 h-20 mx-auto bg-primary-100 rounded-full flex items-center justify-center">
            <Sparkles className="w-10 h-10 text-primary-600 animate-pulse" />
          </div>
          {/* Sparkle effects */}
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-accent-400 rounded-full animate-ping" />
          <div
            className="absolute -bottom-1 -left-3 w-3 h-3 bg-primary-400 rounded-full animate-ping"
            style={{ animationDelay: '0.5s' }}
          />
          <div
            className="absolute top-0 -left-4 w-2 h-2 bg-accent-300 rounded-full animate-ping"
            style={{ animationDelay: '1s' }}
          />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Building Your Brand Profile
        </h2>
        <p className="text-gray-500 mb-8">for {businessName}</p>

        {/* Rotating message */}
        <div className="h-8 flex items-center justify-center">
          <p
            key={currentMessageIndex}
            className="text-lg text-primary-600 font-medium animate-fade-in"
          >
            {LOADING_MESSAGES[currentMessageIndex]}
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 mt-8">
          {LOADING_MESSAGES.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index <= currentMessageIndex
                  ? 'bg-primary-500 scale-100'
                  : 'bg-gray-200 scale-75'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
